import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Sparkles, GraduationCap, Map, Users, UserRound, Clock, ArrowRight } from "lucide-react";

const STAGE_LABELS = {
  coursework: "Coursework", comps: "Comprehensive Exams", topic_development: "Topic Development",
  proposal: "Proposal Development", proposal_defense: "Proposal Defense", irb: "IRB",
  research: "Research", analysis: "Analysis", dissertation: "Dissertation Writing",
  final_review: "Final Review", defense: "Defense", revisions: "Revisions",
  graduation: "Graduation", publishing: "Publishing",
};
const ORDER = Object.keys(STAGE_LABELS);

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [milestones, setMilestones] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [myCircles, setMyCircles] = useState([]);
  const [peers, setPeers] = useState([]);
  // Prevent onboarding-flash bug: keep empty-state suppressed until the
  // first fetch has resolved. Otherwise a fully-onboarded user briefly
  // sees "Complete your profile…" during initial render.
  const [circlesLoaded, setCirclesLoaded] = useState(false);
  const [peersLoaded, setPeersLoaded] = useState(false);

  const loadCircles = async () => {
    try {
      const [rec, mine, ppl] = await Promise.all([
        api.get("/circles/recommended"),
        api.get("/circles", { params: { mine: true } }),
        api.get("/people/recommended", { params: { limit: 4 } }),
      ]);
      setRecommended(rec.data);
      setMyCircles(mine.data);
      setPeers(ppl.data.results || []);
    } catch {
      /* silent: dashboard is best-effort; empty state renders otherwise */
    } finally {
      setCirclesLoaded(true);
      setPeersLoaded(true);
    }
  };

  useEffect(() => {
    if (!user) return;
    if (!user.onboarding_complete) { nav("/onboarding"); return; }
    api.get("/journey/milestones").then((r) => setMilestones(r.data.milestones)).catch(() => {});
    loadCircles();
  }, [user, nav]);

  if (!user) return null;
  const stage = user.current_stage || user.stage || "coursework";
  const idx = ORDER.indexOf(stage);
  const next = ORDER[Math.min(idx + 1, ORDER.length - 1)];
  const completed = milestones.filter((m) => m.status === "COMPLETED").length;
  const pct = Math.round((completed / ORDER.length) * 100);

  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  const myIds = new Set(myCircles.map((c) => c.id));
  const recToShow = recommended.filter((c) => !myIds.has(c.id)).slice(0, 5);

  const joinFromDashboard = async (c) => {
    try {
      await api.post(`/circles/${c.id}/join`);
      await loadCircles();
    } catch { /* toast handled inside Circles page normally; silent here */ }
  };

  return (
    <div className="p-8 lg:p-12 max-w-6xl" data-testid="dashboard-page">
      <p className="text-xs uppercase tracking-[0.25em] text-primary">{greet}</p>
      <h1 className="font-serif text-4xl lg:text-5xl font-black tracking-tight mt-3">{greet}, {user.first_name || user.name?.split(" ")[0] || "candidate"}.</h1>
      <div className="mt-4 text-sm text-muted-foreground space-y-0.5">
        <p>{user.degree_type || user.program} — {user.discipline || user.field_of_study}</p>
        {user.university && <p>{user.university}</p>}
        <p>{(user.methodology || "Undecided") + (user.methodology_subtype ? ` · ${user.methodology_subtype}` : "")}</p>
        {user.expected_graduation_year && <p>Expected Graduation: {user.expected_graduation_year}</p>}
      </div>

      <div className="mt-10 grid md:grid-cols-2 gap-6">
        <Card className="p-6 lg:p-8" data-testid="dash-journey-card">
          <div className="flex items-center gap-2 mb-4"><Map className="h-5 w-5 text-primary" /><h2 className="font-serif text-xl font-black">Your journey</h2></div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Current stage</p>
          <p className="font-serif text-2xl font-black mt-1">{STAGE_LABELS[stage] || stage}</p>
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs mb-1"><span>Overall progress</span><span className="text-muted-foreground">{completed}/{ORDER.length}</span></div>
            <div className="h-2 bg-secondary rounded-full overflow-hidden"><div className="bg-primary h-full transition-all" style={{ width: `${pct}%` }} /></div>
          </div>
          <p className="text-xs text-muted-foreground mt-4">Next milestone: <span className="text-foreground font-medium">{STAGE_LABELS[next]}</span></p>
          <Link to="/journey"><Button variant="outline" size="sm" className="mt-5 rounded-full" data-testid="dash-open-journey">Open journey <ArrowRight className="h-3 w-3 ml-1" /></Button></Link>
        </Card>

        <Card className="p-6 lg:p-8" data-testid="dash-profile-card">
          <div className="flex items-center gap-2 mb-4"><GraduationCap className="h-5 w-5 text-primary" /><h2 className="font-serif text-xl font-black">Your doctoral profile</h2></div>
          <dl className="text-sm space-y-2">
            <div><dt className="text-xs uppercase tracking-widest text-muted-foreground">Degree</dt><dd>{user.degree_type || user.program}</dd></div>
            <div><dt className="text-xs uppercase tracking-widest text-muted-foreground">Discipline</dt><dd>{user.discipline || user.field_of_study || "—"}</dd></div>
            <div><dt className="text-xs uppercase tracking-widest text-muted-foreground">University</dt><dd>{user.university || "—"}</dd></div>
            <div><dt className="text-xs uppercase tracking-widest text-muted-foreground">Methodology</dt><dd>{user.methodology}{user.methodology_subtype ? ` · ${user.methodology_subtype}` : ""}</dd></div>
            <div><dt className="text-xs uppercase tracking-widest text-muted-foreground">Interests</dt><dd>{(user.research_interests || []).join(", ") || "—"}</dd></div>
          </dl>
          <Link to="/profile"><Button variant="outline" size="sm" className="mt-5 rounded-full" data-testid="dash-open-profile">Edit profile</Button></Link>
        </Card>

        <Card className="p-6 lg:p-8" data-testid="dash-circles-card">
          <div className="flex items-center gap-2 mb-2"><Users className="h-5 w-5 text-primary" /><h2 className="font-serif text-xl font-black">Circles for you</h2></div>
          <p className="text-xs text-muted-foreground mb-4">Recommendations use your profile only — no algorithms.</p>
          {myCircles.length > 0 && (
            <>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Joined</p>
              <ul className="space-y-1 mb-4" data-testid="dash-joined-circles">
                {myCircles.slice(0, 4).map((c) => (
                  <li key={c.id}>
                    <Link to={`/circles/${c.id}`} className="flex items-center justify-between border border-border rounded-md px-3 py-2 text-sm hover:bg-secondary transition-colors" data-testid={`dash-joined-${c.slug}`}>
                      <span className="truncate">{c.name}</span>
                      <ArrowRight className="h-3 w-3 text-muted-foreground" />
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
          {recToShow.length > 0 ? (
            <>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Recommended</p>
              <ul className="space-y-2" data-testid="dash-recommended-circles">
                {recToShow.map((c) => (
                  <li key={c.id} className="border border-border rounded-md px-3 py-2 text-sm" data-testid={`dash-rec-${c.slug}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <Link to={`/circles/${c.id}`} className="font-medium hover:underline underline-offset-4 block truncate">{c.name}</Link>
                        {c.reasons?.[0] && <p className="text-[11px] text-muted-foreground truncate">{c.reasons[0]}</p>}
                      </div>
                      <Button size="sm" variant="outline" className="h-7 px-3 rounded-full text-[11px]" onClick={() => joinFromDashboard(c)} data-testid={`dash-join-${c.slug}`}>Join</Button>
                    </div>
                  </li>
                ))}
              </ul>
              <Link to="/circles"><Button variant="outline" size="sm" className="mt-4 rounded-full" data-testid="dash-explore-circles">Explore all Circles <ArrowRight className="h-3 w-3 ml-1" /></Button></Link>
            </>
          ) : !circlesLoaded ? (
            <div className="space-y-2" data-testid="dash-circles-loading">
              <div className="h-8 rounded-md bg-secondary/60 animate-pulse" />
              <div className="h-8 rounded-md bg-secondary/60 animate-pulse" />
              <div className="h-8 rounded-md bg-secondary/60 animate-pulse w-2/3" />
            </div>
          ) : myCircles.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="dash-circles-empty">Complete your profile to see personalized Circle recommendations.</p>
          ) : (
            <Link to="/circles"><Button variant="outline" size="sm" className="mt-2 rounded-full" data-testid="dash-explore-circles">Explore all Circles <ArrowRight className="h-3 w-3 ml-1" /></Button></Link>
          )}
        </Card>

        <Card className="p-6 lg:p-8" data-testid="dash-path-card">
          <div className="flex items-center gap-2 mb-2"><UserRound className="h-5 w-5 text-primary" /><h2 className="font-serif text-xl font-black">People walking your path</h2></div>
          <p className="text-xs text-muted-foreground mb-4">Deterministic scholarly matches — no algorithms, no engagement metrics.</p>
          {peers.length === 0 ? (
            !peersLoaded ? (
              <div className="space-y-2" data-testid="dash-path-loading">
                <div className="h-9 rounded-md bg-secondary/60 animate-pulse" />
                <div className="h-9 rounded-md bg-secondary/60 animate-pulse" />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="dash-path-empty">Finish your profile to unlock peer discovery — discipline, methodology, and stage power the match.</p>
            )
          ) : (
            <>
              <ul className="space-y-2" data-testid="dash-path-list">
                {peers.slice(0, 4).map((p) => (
                  <li key={p.id} className="border border-border rounded-md px-3 py-2" data-testid={`dash-peer-${p.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <Link to={`/people/${p.id}`} className="font-medium hover:underline underline-offset-4 block truncate">{p.display_name || "Doctoral Candidate"}</Link>
                        <p className="text-[11px] text-muted-foreground truncate">{[p.degree_type, p.discipline, p.methodology_subtype].filter(Boolean).join(" · ")}</p>
                        {p.match?.reasons?.[0] && <p className="text-[11px] text-primary/80 truncate mt-0.5">{p.match.reasons[0]}</p>}
                      </div>
                      <span className="text-[10px] uppercase tracking-widest text-muted-foreground shrink-0">{(p.match?.band || "").replace("_MATCH", "").replace("_", " ").toLowerCase()}</span>
                    </div>
                  </li>
                ))}
              </ul>
              <Link to="/people"><Button variant="outline" size="sm" className="mt-4 rounded-full" data-testid="dash-see-more-people">See more people <ArrowRight className="h-3 w-3 ml-1" /></Button></Link>
            </>
          )}
        </Card>

        <Card className="p-6 lg:p-8 md:col-span-2 flex flex-col md:flex-row items-start gap-6 bg-secondary/60" data-testid="dash-ai-card">
          <Sparkles className="h-8 w-8 text-primary shrink-0" />
          <div className="flex-1">
            <h3 className="font-serif text-2xl font-black">DocCircle AI</h3>
            <p className="text-muted-foreground mt-2 max-w-2xl">Your doctoral research companion — a private companion for methodology, IRB preparation, qualitative and quantitative analysis, academic writing, and defense prep.</p>
            <p className="text-[10px] uppercase tracking-widest text-primary mt-3">Coming soon</p>
          </div>
        </Card>
      </div>
    </div>
  );
}
